import { CommonModule } from '@angular/common';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, GridOptions } from '@ag-grid-community/core';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { CustomAgGridOptions, FieldTypes } from '@digiwall/ag-grid';
import {
  BaseDocLineDto,
  CustomerDeliveryNoteDto,
  DocLineRole,
} from '@digiwall/goliath-documents/proxy';
import { GroupRowCellRendererComponent } from '@digiwall/goliath-core';

@Component({
  selector: 'document-customer-invoice-preview-grid',
  templateUrl: './customer-invoice-preview-grid.component.html',
  styleUrl: './customer-invoice-preview-grid.component.scss',
  imports: [CommonModule, AgGridAngular, LocalizationModule],
})
export class CustomerInvoicePreviewGridComponent implements OnInit {
  @ViewChild('grid') grid: AgGridAngular;

  @Input() set deliveryNotes(value) {
    this._deliveryNotes = value;
  }

  @Input() set lines(value) {
    this._lines = value;
    this.grid?.api?.setGridOption('rowData', this._lines);
    this.grid?.api?.redrawRows();
    this.computeTotalRows();
  }

  _lines: BaseDocLineDto[] = [];
  _deliveryNotes: CustomerDeliveryNoteDto[] = [];

  get lines() {
    return this._lines;
  }

  get deliveryNotes() {
    return this._deliveryNotes;
  }

  gridOptions: GridOptions | undefined;

  defaultColDef = {
    floatingFilter: false,
    sortable: false,
  };

  columnDefs: ColDef[] = [
    {
      showRowGroup: 'lineFromId',
      cellRenderer: GroupRowCellRendererComponent,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      suppressMovable: true,
      width: 50,
      resizable: false,
    },
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 50,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      sort: 'asc',
      pinned: 'left',
      suppressMovable: true,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
    },
    {
      field: 'articleSalesUnitCode',
      headerName: this.localizationService.instant('Product::Article:ProductSaleUnit'),
      type: FieldTypes.String,
    },
    {
      field: 'quantity',
      editable: true,
      headerName: this.localizationService.instant('::Quantity'),
      type: FieldTypes.Number,
    },
    {
      field: 'totalPrice',
      headerName: this.localizationService.instant('Doc::TotalPrice'),
      type: FieldTypes.Currency,
    },
  ];

  totalRowHTVA = {
    id: 'totalRowHTVA',
    bottomRow: true,
    description: this.localizationService.instant('Doc::TotalHTVA'),
    totalPrice: 0,
    isBottom: true,
  };

  totalRowTVA = {
    id: 'totalRowTVA',
    bottomRow: true,
    description: this.localizationService.instant('Doc::TotalTVA'),
    totalPrice: 0,
    isBottom: true,
  };

  pinnedBottomRowData: any[] = [this.totalRowHTVA, this.totalRowTVA];

  constructor(
    private localizationService: LocalizationService,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    this.gridOptions = {
      context: this,
      rowModelType: 'clientSide',
      autoSizeStrategy: {
        type: 'fitGridWidth',
        defaultMinWidth: 40,
      },
      pagination: false,
      enableAdvancedFilter: false,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      rowHeight: 30,
      suppressContextMenu: false,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
      },
      getContextMenuItems: _ => [],
      getRowId: ({ data }: any) => {
        return data.id;
      },
      components: CustomAgGridOptions.Components,
      columnTypes: CustomAgGridOptions.ColumnTypeDefinitions(this.injector),
      rowData: this.lines,

      treeData: true,
      getDataPath: this.getDataPath,
      groupDisplayType: 'custom',
      groupDefaultExpanded: 1,
      groupAllowUnbalanced: true,

      pinnedBottomRowData: this.pinnedBottomRowData,
    };
  }

  getDataPath = (line, paths: string[] = []) => {
    if (line == null) return paths;

    if (line.lineFromId != null) {
      paths = this.getDataPath(
        this.lines.find(x => x.id == line.lineFromId),
        paths,
      );
    }
    paths.push(line.id);
    return paths;
  };

  getRowClass = params => {
    const line = params.node.data;

    if (line == null) return;

    let classes = [];

    if (line.bottomRow) {
      classes.push('footer-row');
    }
    if (line.lineRole == DocLineRole.DocChapter) {
      const chapterClass = this.lines.some(
        parentLine =>
          parentLine.id == line.lineFromId && parentLine.lineRole == DocLineRole.DocChapter,
      )
        ? 'sub-chapter-line'
        : 'chapter-line';

      classes.push(chapterClass);
    }
    return classes;
  };

  private computeTotalRows() {
    const articleLines = this.lines?.filter(
      line => line.lineRole == DocLineRole.CustomerInvoiceArticle,
    );

    let totalPriceHTVA = 0;

    articleLines?.forEach((articleLine: any) => {
      totalPriceHTVA += articleLine.totalPrice;
    });

    this.totalRowHTVA.totalPrice = totalPriceHTVA;
    this.totalRowTVA.totalPrice = this.deliveryNotes
      .map(deliveryNote => deliveryNote.totalAmountVatIncluded)
      .reduce((prevAmount, currentAmount) => prevAmount + currentAmount, 0);

    let priceHTVARowNode = this.grid?.api.getPinnedBottomRow(0);
    let priveTVARowNode = this.grid?.api.getPinnedBottomRow(1);

    this.grid?.api.redrawRows({ rowNodes: [priceHTVARowNode, priveTVARowNode] });
  }
}
